import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import React from 'react';

import type { DispositionHelperResult } from '@/hooks/useDispositionHelper';
import {
  compareSortKeys,
  type FieldSchemaOrderMap,
  getAskNextSortKey,
} from '@/lib/disposition/fieldSchemaOrder';
import { resolveAskNextLabel } from '@/lib/disposition/resolveAskNextLabel';

interface DispositionHelperPanelProps {
  result: DispositionHelperResult;
  /**
   * Workflow output-schema labels keyed by field id. Used for ask-next chip
   * display so labels match the form. Falls back to the hardcoded engine-key
   * label map and finally to the raw key.
   */
  fieldLabels?: Record<string, string>;
  /**
   * Nested child-index map derived from the output schema. When provided,
   * the "Ask next" chips are sorted by Nomos canonical order (with array
   * items grouped together) instead of the engine's native field order.
   */
  fieldOrder?: FieldSchemaOrderMap;
  /** Optional click handler to scroll/focus a specific field in the form. */
  onJumpToField?: (fieldKey: string) => void;
  /**
   * Optional handler for an "Accept" button next to the suggested disposition.
   * When provided, a button is rendered that lets the user apply the suggestion
   * to the current call disposition. The parent decides whether the suggestion
   * maps to a known option and only passes this handler when it can act on it.
   */
  onAcceptSuggestion?: () => void;
  /** Disables the Accept button (e.g. not in edit mode, already accepted). */
  acceptDisabled?: boolean;
  /** Tooltip text shown on the Accept button (used to explain disabled state). */
  acceptDisabledTitle?: string;
  /**
   * When false, the "Ask next" chip list is hidden. The agent can only act on
   * those chips while editing the form, so we hide them in read-only mode.
   */
  showAskNext?: boolean;
  /**
   * When false, the suggested-disposition row is omitted — the parent renders
   * it elsewhere (inline beside the Disposition label) via
   * DispositionSuggestion instead.
   */
  showSuggestion?: boolean;
  className?: string;
}

interface DispositionSuggestionProps {
  result: DispositionHelperResult;
  onAcceptSuggestion?: () => void;
  acceptDisabled?: boolean;
  acceptDisabledTitle?: string;
  /**
   * When false, only the sparkles icon marks the row as a suggestion — used
   * where the row sits inline beside the Disposition label and horizontal
   * space is tight.
   */
  showCaption?: boolean;
  className?: string;
}

export const DispositionSuggestion: React.FC<DispositionSuggestionProps> = ({
  result,
  onAcceptSuggestion,
  acceptDisabled,
  acceptDisabledTitle,
  showCaption = true,
  className,
}) => {
  const { supported, current } = result;

  if (!supported || !current) return null;

  return (
    <div className={`flex items-center gap-2 flex-wrap min-w-0 ${className ?? ''}`}>
      <span
        className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1 shrink-0"
        title={showCaption ? undefined : 'Suggested disposition'}
      >
        <Sparkles className="w-3 h-3" />
        {showCaption && 'Suggested'}
      </span>
      <Badge
        variant={current.matched_scenario ? 'success' : 'secondary'}
        className="min-w-0"
        title={current.disposition}
      >
        <span className="truncate">{current.disposition}</span>
      </Badge>
      {onAcceptSuggestion && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAcceptSuggestion}
          disabled={acceptDisabled}
          title={acceptDisabledTitle}
          className="h-6 px-2 text-xs shrink-0"
        >
          Accept
        </Button>
      )}
    </div>
  );
};

export const DispositionHelperPanel: React.FC<DispositionHelperPanelProps> = ({
  result,
  fieldLabels,
  fieldOrder,
  onJumpToField,
  onAcceptSuggestion,
  acceptDisabled,
  acceptDisabledTitle,
  showAskNext = true,
  showSuggestion = true,
  className,
}) => {
  const { supported, current, missingFields } = result;

  // Collapsed by default so the assist strip doesn't crowd out the form
  // fields below; the header button expands the chips on demand.
  const [askNextExpanded, setAskNextExpanded] = React.useState(false);

  // Sort by Nomos schema order when we have a non-empty order map. Falls back
  // to engine-native order so the chips still render if the schema hasn't
  // loaded yet. Hook is declared before the early-return below so call order
  // stays stable across renders.
  const sortedMissingFields = React.useMemo(() => {
    if (!fieldOrder || fieldOrder.size === 0) return missingFields;
    const keyed = missingFields.map(field => ({
      field,
      key: getAskNextSortKey(field, fieldOrder),
    }));
    keyed.sort((a, b) => compareSortKeys(a.key, b.key));
    return keyed.map(entry => entry.field);
  }, [missingFields, fieldOrder]);

  if (!supported || !current) return null;

  const missingCount = missingFields.length;
  const showAskNextSection = showAskNext && missingCount > 0;

  if (!showSuggestion && !showAskNextSection) return null;

  const resolveLabel = (key: string): string => resolveAskNextLabel(key, fieldLabels);

  return (
    <div className={`space-y-3 ${className ?? ''}`} data-testid="disposition-helper-panel">
      {showSuggestion && (
        <DispositionSuggestion
          result={result}
          onAcceptSuggestion={onAcceptSuggestion}
          acceptDisabled={acceptDisabled}
          acceptDisabledTitle={acceptDisabledTitle}
        />
      )}

      {showAskNextSection && (
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={() => setAskNextExpanded(expanded => !expanded)}
            aria-expanded={askNextExpanded}
            aria-label={askNextExpanded ? 'Collapse ask-next fields' : 'Expand ask-next fields'}
            className="flex w-full items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            <span>Ask next ({missingCount})</span>
            {askNextExpanded ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </button>
          {/* Cap to ~3 rows of chips; scroll vertically beyond that to avoid
              the panel taking over the viewport when there are many fields. */}
          {askNextExpanded && (
            <div className="flex flex-wrap gap-1 max-h-[5.5rem] overflow-y-auto pr-1">
              {sortedMissingFields.map(fieldKey => {
                const label = resolveLabel(fieldKey);
                return (
                  <button
                    key={fieldKey}
                    type="button"
                    onClick={onJumpToField ? () => onJumpToField(fieldKey) : undefined}
                    disabled={!onJumpToField}
                    className="inline-flex items-center rounded-md border bg-background px-2 py-0.5 text-xs text-foreground hover:bg-muted disabled:cursor-default disabled:hover:bg-background"
                    title={fieldKey === label ? undefined : fieldKey}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
